/**
 * <sub-brand-slider> — auto-scrolling marquee die óók werkt als swipe-slider.
 * Visibility/reduced-motion/pause delegeert naar AutoplayController
 * (assets/autoplay-controller.js).
 */
if (!window.__SubBrandSliderLoaded) {
window.__SubBrandSliderLoaded = true;
class SubBrandSlider extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-initialized')) return;
    this.setAttribute('data-initialized', '');

    this.track = this.querySelector('[data-sbs-track]');
    this.prev = this.querySelector('[data-sbs-prev]');
    this.next = this.querySelector('[data-sbs-next]');
    if (!this.track) return;

    this.pps = Number(this.getAttribute('data-pps')) || 0;
    this._interactCooldown = 0;
    this._rafId = null;
    this._lastTs = 0;

    this._ctrl = new AutoplayController(this, {
      onEnable:  () => this._startLoop(),
      onDisable: () => this._stopLoop(),
    });

    this._setupButtons();
    this._setupDrag();
  }

  disconnectedCallback() {
    this._stopLoop();
    this._ctrl?.destroy();
    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
  }

  _startLoop() {
    if (this.pps <= 0) return;
    this._lastTs = 0;
    this._prevScrollBehavior = this.track.style.scrollBehavior;
    this.track.style.scrollBehavior = 'auto';
    const tick = (ts) => {
      if (!this._lastTs) this._lastTs = ts;
      const dt = Math.min(ts - this._lastTs, 100);
      this._lastTs = ts;

      if (performance.now() >= this._interactCooldown) {
        const halfWidth = this.track.scrollWidth / 2;
        if (halfWidth > 0) {
          const pxPerMs = this.pps / 1000;
          this.track.scrollLeft += pxPerMs * dt;
          if (this.track.scrollLeft >= halfWidth) {
            this.track.scrollLeft -= halfWidth;
          }
        }
      }
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  _stopLoop() {
    cancelAnimationFrame(this._rafId);
    this._rafId = null;
    if (this._prevScrollBehavior !== undefined) {
      this.track.style.scrollBehavior = this._prevScrollBehavior;
    }
  }

  _cardWidth() {
    const card = this.track.querySelector('.sub-brand-card');
    if (!card) return this.track.clientWidth / 3;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.offsetWidth + gap;
  }

  _scrollBy(delta) {
    this._interactCooldown = performance.now() + 2000;
    this.track.scrollBy({ left: delta, behavior: this._ctrl?.reduced ? 'auto' : 'smooth' });
  }

  _setupButtons() {
    this.prev?.addEventListener('click', () => this._scrollBy(-this._cardWidth()));
    this.next?.addEventListener('click', () => this._scrollBy(+this._cardWidth()));
    this.track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this._scrollBy(+this._cardWidth()); }
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this._scrollBy(-this._cardWidth()); }
    });
  }

  _setupDrag() {
    const DRAG_THRESHOLD = 6;
    let startX = 0, startScroll = 0, dragging = false, pressed = false;

    const onDown = (e) => {
      if (e.pointerType !== 'mouse') return;
      if (e.button !== 0) return;
      pressed = true;
      dragging = false;
      startX = e.clientX;
      startScroll = this.track.scrollLeft;
    };
    this._onPointerMove = (e) => {
      if (!pressed || e.pointerType !== 'mouse') return;
      const dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        this._ctrl?.pause();
        this.track.classList.add('is-dragging');
      }
      this.track.scrollLeft = startScroll - dx;
      e.preventDefault();
    };
    this._onPointerUp = () => {
      if (!pressed) return;
      pressed = false;
      if (dragging) {
        this.track.classList.remove('is-dragging');
        this._ctrl?.resume();
        this._interactCooldown = performance.now() + 1500;
        const clickGuard = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
        this.track.addEventListener('click', clickGuard, { capture: true, once: true });
        setTimeout(() => this.track.removeEventListener('click', clickGuard, true), 400);
      }
      dragging = false;
    };

    this.track.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', this._onPointerMove);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);

    this.track.addEventListener('wheel', () => {
      this._interactCooldown = performance.now() + 1500;
    }, { passive: true });
    this.track.addEventListener('touchstart', () => {
      this._ctrl?.pause();
    }, { passive: true });
    this.track.addEventListener('touchend', () => {
      this._ctrl?.resume();
      this._interactCooldown = performance.now() + 1500;
    });
  }
}

if (!customElements.get('sub-brand-slider')) {
  customElements.define('sub-brand-slider', SubBrandSlider);
}
}
